import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Images, Plus, Loader2, FileImage, Upload, GripVertical, 
  Star, Eye, ArrowUp, ArrowDown, Trash2, CheckCircle, 
  AlertCircle, X
} from 'lucide-react';

const BusinessGallerySection = ({ 
  searchedBusiness, 
  businessGalleryContext, 
  adminId, 
  createTracker 
}) => {
  // Destructure SPECIFIC functions to avoid infinite loops in useEffect
  const { 
    getGalleryImages, 
    getGalleryStats, 
    uploadGalleryImage, 
    uploadMultipleImages,
    deleteImage,
    setAsPrimaryImage,
    moveImageToPosition,
    swapImagePositions,
    formatFileSize
  } = businessGalleryContext;

  // ============================================================
  // LOCAL STATE
  // ============================================================
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryStats, setGalleryStats] = useState(null);
  const [isLoadingGallery, setIsLoadingGallery] = useState(false);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);
  
  // Interaction State
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [draggedImage, setDraggedImage] = useState(null);
  
  // Feedback State
  const [galleryError, setGalleryError] = useState(null);
  const [gallerySuccess, setGallerySuccess] = useState(null);
  
  const galleryFileInputRef = useRef(null);

  // ============================================================
  // EFFECTS & DATA LOADING
  // ============================================================

  // Load Gallery Images - Depends ONLY on the specific fetch functions
  const loadGalleryImages = useCallback(async (regNum) => {
    if (!regNum) return;
    
    setIsLoadingGallery(true);
    setGalleryError(null);
    try {
      const result = await getGalleryImages(regNum);
      setGalleryImages(result.images || []);

      const stats = await getGalleryStats(regNum);
      setGalleryStats(stats.stats || null);
    } catch (e) {
      console.error("Error loading gallery", e);
      setGalleryImages([]);
      setGalleryStats(null);
    } finally {
      setIsLoadingGallery(false);
    }
  }, [getGalleryImages, getGalleryStats]);

  // Initial Load
  useEffect(() => {
    if (searchedBusiness?.business_registry_number) {
      loadGalleryImages(searchedBusiness.business_registry_number);
    }
  }, [searchedBusiness, loadGalleryImages]);

  // Clear messages automatically
  useEffect(() => {
    if (galleryError) {
      const timer = setTimeout(() => setGalleryError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [galleryError]);

  useEffect(() => {
    if (gallerySuccess) {
      const timer = setTimeout(() => setGallerySuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [gallerySuccess]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const handleGalleryUpload = async (event) => {
    if (!event.target.files || event.target.files.length === 0) return;
    if (!searchedBusiness) return;

    const files = Array.from(event.target.files);
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024;

    const invalidFiles = files.filter(f => !validTypes.includes(f.type) || f.size > maxSize);
    if (invalidFiles.length > 0) {
      setGalleryError(`Algunos archivos no son válidos. Tipos permitidos: JPEG, PNG, GIF, WebP. Tamaño máximo: 10MB.`);
      if (galleryFileInputRef.current) galleryFileInputRef.current.value = '';
      return;
    }

    setIsUploadingGallery(true);
    setGalleryError(null);

    try {
      if (files.length === 1) {
        // Single Upload
        await uploadGalleryImage(
          searchedBusiness.business_registry_number,
          files[0]
        );
        setGallerySuccess(`Imagen "${files[0].name}" subida exitosamente`);

        // Track
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'gallery_image_upload',
          change_reason: `Gallery image added for ${searchedBusiness.business_registry_number} (${searchedBusiness.business_name}). File: ${files[0].name}`
        });
      } else {
        // Bulk Upload
        const results = await uploadMultipleImages(
          searchedBusiness.business_registry_number,
          files
        );

        const successCount = results.successful.length;
        const failCount = results.failed.length;

        if (failCount > 0) {
          setGalleryError(`${successCount} imágenes subidas, ${failCount} fallaron`);
        } else {
          setGallerySuccess(`${successCount} imágenes subidas exitosamente`);
        }

        // Track
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'gallery_bulk_upload',
          change_reason: `Bulk gallery upload for ${searchedBusiness.business_registry_number}: ${successCount} successful, ${failCount} failed`
        });
      }

      await loadGalleryImages(searchedBusiness.business_registry_number);

    } catch (error) {
      console.error('Error uploading gallery images:', error);
      setGalleryError(error.message || 'Error al subir imágenes');
    } finally {
      setIsUploadingGallery(false);
      if (galleryFileInputRef.current) galleryFileInputRef.current.value = '';
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!searchedBusiness) return;
    const imageToDelete = galleryImages.find(img => img.id === imageId);

    try {
      await deleteImage(imageId, true);
      setGallerySuccess('Imagen eliminada exitosamente');
      setShowDeleteConfirm(null);

      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'gallery_image_delete',
        change_reason: `Gallery image deleted for ${searchedBusiness.business_registry_number}. Image ID: ${imageId}, Filename: ${imageToDelete?.original_filename || 'unknown'}`
      });

      await loadGalleryImages(searchedBusiness.business_registry_number);

    } catch (error) {
      console.error('Error deleting gallery image:', error);
      setGalleryError(error.message || 'Error al eliminar imagen');
    }
  };

  const handleSetAsPrimary = async (imageId) => {
    if (!searchedBusiness) return;

    try {
      await setAsPrimaryImage(searchedBusiness.business_registry_number, imageId);
      setGallerySuccess('Imagen establecida como principal');

      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'gallery_primary_change',
        change_reason: `Primary gallery image changed for ${searchedBusiness.business_registry_number}. New primary image ID: ${imageId}`
      });

      await loadGalleryImages(searchedBusiness.business_registry_number);
    } catch (error) {
      console.error('Error setting primary image:', error);
      setGalleryError(error.message || 'Error al establecer imagen principal');
    }
  };

  // Reordering Logic
  const handleMoveUp = async (imageId) => {
    const currentIndex = galleryImages.findIndex(img => img.id === imageId);
    if (currentIndex <= 0) return;
    const newPosition = currentIndex; 

    try {
      await moveImageToPosition(
        searchedBusiness.business_registry_number,
        imageId,
        newPosition
      );
      await loadGalleryImages(searchedBusiness.business_registry_number);
    } catch (error) {
      setGalleryError(error.message || 'Error al mover imagen');
    }
  };

  const handleMoveDown = async (imageId) => {
    const currentIndex = galleryImages.findIndex(img => img.id === imageId);
    if (currentIndex >= galleryImages.length - 1) return;
    const newPosition = currentIndex + 2; 

    try {
      await moveImageToPosition(
        searchedBusiness.business_registry_number,
        imageId,
        newPosition
      );
      await loadGalleryImages(searchedBusiness.business_registry_number);
    } catch (error) {
      setGalleryError(error.message || 'Error al mover imagen');
    }
  };

  const handleDragStart = (e, image) => {
    setDraggedImage(image);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetImage) => {
    e.preventDefault();
    if (!draggedImage || draggedImage.id === targetImage.id) {
      setDraggedImage(null);
      return;
    }

    try {
      await swapImagePositions(
        searchedBusiness.business_registry_number,
        draggedImage.id,
        targetImage.id
      );

      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'gallery_reorder',
        change_reason: `Gallery images reordered for ${searchedBusiness.business_registry_number}.`
      });

      await loadGalleryImages(searchedBusiness.business_registry_number);
    } catch (error) {
      console.error('Error reordering images:', error);
      setGalleryError(error.message || 'Error al reordenar imágenes');
    } finally {
      setDraggedImage(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedImage(null);
  };

  // ============================================================
  // RENDER HELPERS
  // ============================================================

  const GalleryImageCard = ({ image, index }) => {
    const isFirst = index === 0;
    const isLast = index === galleryImages.length - 1;
    const isDragging = draggedImage?.id === image.id;

    return (
      <div
        draggable
        onDragStart={(e) => handleDragStart(e, image)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, image)}
        onDragEnd={handleDragEnd}
        className={`relative group bg-white rounded-lg border-2 overflow-hidden transition-all duration-200 ${
          isDragging
            ? 'border-orange-500 opacity-50 scale-95'
            : 'border-gray-200 hover:border-orange-300 hover:shadow-lg'
        } ${isFirst ? 'ring-2 ring-orange-400 ring-offset-2' : ''}`}
      >
        {isFirst && (
          <div className="absolute top-2 left-2 z-10 bg-orange-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Star className="h-3 w-3" />
            Principal
          </div>
        )}

        <div className="absolute top-2 right-2 z-10 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
          #{index + 1}
        </div>

        <div className="absolute top-1/2 left-2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
          <GripVertical className="h-5 w-5 text-gray-400" />
        </div>

        <div className="aspect-square bg-gray-100">
          <img
            src={image.cloudflare_image_url}
            alt={image.original_filename || `Image ${index + 1}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedGalleryImage(image)}
              className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
              title="Ver imagen"
            >
              <Eye className="h-4 w-4 text-gray-700" />
            </button>

            {!isFirst && (
              <button
                onClick={() => handleSetAsPrimary(image.id)}
                className="p-2 bg-white rounded-full hover:bg-orange-100 transition-colors"
                title="Establecer como principal"
              >
                <Star className="h-4 w-4 text-orange-500" />
              </button>
            )}

            {!isFirst && (
              <button
                onClick={() => handleMoveUp(image.id)}
                className="p-2 bg-white rounded-full hover:bg-blue-100 transition-colors"
                title="Mover arriba"
              >
                <ArrowUp className="h-4 w-4 text-blue-500" />
              </button>
            )}

            {!isLast && (
              <button
                onClick={() => handleMoveDown(image.id)}
                className="p-2 bg-white rounded-full hover:bg-blue-100 transition-colors"
                title="Mover abajo"
              >
                <ArrowDown className="h-4 w-4 text-blue-500" />
              </button>
            )}

            <button
              onClick={() => setShowDeleteConfirm(image.id)}
              className="p-2 bg-white rounded-full hover:bg-red-100 transition-colors"
              title="Eliminar imagen"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          </div>
        </div>

        <div className="p-2 border-t border-gray-100">
          <p className="text-xs text-gray-500 truncate" title={image.original_filename}>
            {image.original_filename || 'Sin nombre'}
          </p>
          <p className="text-xs text-gray-400">
            {image.file_size ? formatFileSize(image.file_size) : 'N/A'}
          </p>
        </div>
      </div>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-orange-200 p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Images className="h-5 w-5 text-orange-500" />
            Galería de Imágenes
          </h3>
          {galleryStats && (
            <p className="text-sm text-gray-500 mt-1">
              {galleryStats.total_images} imágenes • {formatFileSize(galleryStats.total_file_size)}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => galleryFileInputRef.current?.click()}
            disabled={isUploadingGallery}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploadingGallery ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Subiendo...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Agregar Imágenes
              </>
            )}
          </button>
          <input
            type="file"
            ref={galleryFileInputRef}
            className="hidden"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            multiple
            onChange={handleGalleryUpload}
          />
        </div>
      </div>

      {gallerySuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <span className="text-green-700">{gallerySuccess}</span>
        </div>
      )}

      {galleryError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{galleryError}</span>
        </div>
      )}

      {isLoadingGallery && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
          <span className="ml-3 text-gray-500">Cargando galería...</span>
        </div>
      )}

      {!isLoadingGallery && galleryImages.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {galleryImages.map((image, index) => (
            <GalleryImageCard key={image.id} image={image} index={index} />
          ))}
        </div>
      )}

      {!isLoadingGallery && galleryImages.length === 0 && (
        <div className="text-center py-12">
          <FileImage className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-700 mb-2">
            No hay imágenes en la galería
          </h4>
          <p className="text-gray-500 mb-4">
            Sube la primera imagen para comenzar a construir la galería del negocio.
          </p>
          <button
            onClick={() => galleryFileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
          >
            <Upload className="h-4 w-4" />
            Subir Primera Imagen
          </button>
        </div>
      )}

      {!isLoadingGallery && galleryImages.length > 1 && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700 flex items-center gap-2">
            <GripVertical className="h-4 w-4" />
            <span><strong>Tip:</strong> Arrastra y suelta las imágenes para reordenarlas. La primera imagen será la principal.</span>
          </p>
        </div>
      )}

      {/* ============================================================ */}
      {/* IMAGE PREVIEW MODAL */}
      {/* ============================================================ */}
      {selectedGalleryImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedGalleryImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setSelectedGalleryImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={selectedGalleryImage.cloudflare_image_url}
              alt={selectedGalleryImage.original_filename || 'Gallery image'}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-4 rounded-b-lg">
              <p className="font-medium">{selectedGalleryImage.original_filename || 'Sin nombre'}</p>
              <p className="text-sm text-gray-300">
                {selectedGalleryImage.file_size ? formatFileSize(selectedGalleryImage.file_size) : 'N/A'} •
                Subido: {new Date(selectedGalleryImage.created_at).toLocaleDateString('es-PR')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ============================================================ */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowDeleteConfirm(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Eliminar Imagen</h3>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar esta imagen? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteGalleryImage(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessGallerySection;